import React, { Fragment, useEffect, useState as useStateC } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import bemCn from 'bem-cn';
import moment from 'moment-timezone';
import { IMG_RESIZES } from '../../consts/resizer.consts';
import { ImageHelper } from '../../classes/image-helper.class';
import useMobileScreen from '../../services/hooks/useMobileScreen';
import { RouteFormatter } from '../../routes';
import banner1Img from '../../assets/img/blog/blog-banner-1.jpg?sizes[]=600,sizes[]=900';
import banner2Img from '../../assets/img/blog/blog-banner-2.jpg?sizes[]=400,sizes[]=667';
import { BLOG_LIMIT_POSTS, BLOG_POST_DEF_POSTS } from '../../consts/blog.consts';
import BlogArticleDesktopLow from '../../components/blog-article-desktop-low';
import BlogArticleSimple from '../../components/blog-article-simple';
import SpotBreadcrumbs from '../../components/spot-breadcrumbs';
import BlogCategories from '../../components/blog-categories';
import BlogTopSticky from '../../components/blog-top-nav/top-sticky';
import BlogSideBar from '../../components/blog-side-bar';
import SniffButton from '../../components/sniff-button';
import defSpotImg from '../../assets/img/blog/blog-placeholder.png?sizes[]=450,sizes[]=600';
import BlogSocial from '../../components/blog-social';
import BlogTopNav from '../../components/blog-top-nav';
import BlogFooter from '../../components/blog-footer';
import PictureSet from '../../components/picture-set';
import FooterNew from '../../components/footer-new';
import Preloader from '../../components/preloader';
import NotFound from '../not-found';
import SvgChevronRight from 'src/assets/svg/SvgChevronRight.svg';
import SvgChevronLeft from 'src/assets/svg/SvgChevronLeft.svg';
import SvgPromoFlag from 'src/assets/svg/SvgPromoFlag.svg';
import SvgCalendar from 'src/assets/svg/SvgCalendar.svg';
import './post.scss';

const router = require('react-router');
const hook = require('../../services/hooks/useGetBlogPost');
const useGetBlogPosts = require('../../services/hooks/useGetBlogPosts');

const b = bemCn('sniff-blog-post');

const Banner1 = () => (
    <div className={b('banner-1')}>
        <PictureSet imgs={banner1Img} width={900} height={422} alt="Sniffspot Dog running on field" />
        <div className={b('banner-1_content')}>
            <p className="snif-p snif-semibold mb-1 pb-05">Get safe exercise for your dog by renting a private dog park near you</p>
            <p>
                <a href={RouteFormatter.listings({})}>
                    <SniffButton>Explore spots near me</SniffButton>
                </a>
            </p>
        </div>
    </div>
);

const Banner2 = () => (
    <div className={b('banner-2')}>
        <div className={b('banner-2_content')}>
            <p className="snif-p snif-semibold mb-1 pb-05">Get your dog the safe enrichment they need by renting a Sniffspot</p>
            <p>
                <a href={RouteFormatter.listings({})}>
                    <SniffButton>Explore spots</SniffButton>
                </a>
            </p>
        </div>
        <div className={b('banner-2_img')}>
            <PictureSet imgs={banner2Img} width={500} height={667} alt="Sniffspot Dog swimming in pool" />
        </div>
    </div>
);

const Banner1String = renderToStaticMarkup(<Banner1 />);
const Banner2String = renderToStaticMarkup(<Banner2 />);

const blogBanner = (i) => (i % 4 == 0 ? Banner2String : Banner1String);

const getListBread = (data) =>
    data
        ? [
              { name: 'Blog', url: RouteFormatter.blog({}) },
              {
                  name: data.categories[0].title,
                  url: RouteFormatter.blogCategory({ id: data.categories[0].slug }),
              },
              { name: data.title },
          ]
        : [{ name: 'Blog', url: RouteFormatter.blog({}) }];

const BlogSubDetail = ({ blogPost, updatedAt, noMargin, useStateSSR }) => (
    <div className={b('sub-detail').mix(noMargin ? 'mb-0' : '')}>
        <a href={RouteFormatter.blogAuthor({ slug: blogPost.author.slug })} className={b('sub-detail_author')}>
            <PictureSet
                imgs={blogPost.author.photo}
                isAvatar
                width={64}
                height={64}
                useState={useStateSSR}
                SSR={!!useStateSSR}
                alt={blogPost.author.name + ' photo'}
            />
            <p className="snif-s1 snif-p-pc snif-medium">{blogPost.author.name}</p>
        </a>
        <div className={b('sub-detail_published')}>
            <SvgCalendar className="me-2" />
            <p className="snif-s1 snif-p-pc">{updatedAt}</p>
        </div>
        {blogPost?.categories?.map((v) => (
            <a key={v.id} href={RouteFormatter.blogCategory({ id: v.slug })} className={b('sub-detail_tag')}>
                <SvgPromoFlag className="text-deep me-1" width={20} height={20} />
                <p className="snif-s1">{v.title}</p>
            </a>
        ))}
    </div>
);

const BlogAuthorDetails = ({ author, useStateSSR }) => {
    return (
        <div className={b('author-box')}>
            <div className="d-none d-md-flex">
                <PictureSet
                    imgs={author.photo}
                    isAvatar
                    width={144}
                    height={144}
                    useState={useStateSSR}
                    SSR={!!useStateSSR}
                    alt={author.name + ' photo'}
                    className="img-circle"
                />
            </div>
            <div>
                <div className={b('author-box_mob-header')}>
                    <PictureSet
                        imgs={author.photo}
                        isAvatar
                        width={144}
                        height={144}
                        useState={useStateSSR}
                        SSR={!!useStateSSR}
                        alt={author.name + ' photo'}
                        className="d-md-none img-circle"
                    />
                    <div>
                        <a href={RouteFormatter.blogAuthor({ slug: author.slug })}>
                            <h3 className="snif-m1 snif-semibold mb-1">{author.name}</h3>
                        </a>
                        <p className={b('author-box_about')}>About the author</p>
                    </div>
                </div>
                <div className={b('author-box_desc')} dangerouslySetInnerHTML={{ __html: author.description }} />
                <p className="snif-s1 snif-medium text-primary text-underline mt-1">
                    <a href={RouteFormatter.blogAuthor({ slug: author.slug })}>Read more</a>
                </p>
                <BlogSocial {...{ blogAuthor: author }} />
            </div>
        </div>
    );
};

const BlogPost = ({ blogPostSSR, blogCategoriesSSR, useGetBlogPostsSSR, useStateSSR }) => {
    const isMobile = useMobileScreen();
    const { slug } = !useStateSSR && router.useParams();
    const { data, loading: loadingPage } = !useStateSSR && hook.useGetBlogPost(slug);
    const { getBlogPosts, loading } = useStateSSR ? (useGetBlogPostsSSR ? useGetBlogPostsSSR() : {}) : useGetBlogPosts.default();
    const blogPost = blogPostSSR || data?.blogPost;
    const blogCategories = blogCategoriesSSR || data?.blogCategories;
    const useState = useStateSSR || useStateC;
    const listBread = getListBread(blogPost);
    const [limit, setLimit] = useState(BLOG_POST_DEF_POSTS);
    const [recentPosts, setRecentPosts] = useState(blogPost?.recentPosts || []);
    const [maxPost, setMaxPost] = useState(recentPosts ? recentPosts?.length < limit : false);
    const updatedAt = blogPost ? moment(blogPost?.updatedAt).format('MMMM DD, YYYY') : '';
    const thumbnail = blogPost?.thumbnail ? ImageHelper.getUrlFromOptions(blogPost.thumbnail, `fit-in/${IMG_RESIZES.x0450}`) : defSpotImg;
    const previousPost = blogPost?.previousPost;
    const nextPost = blogPost?.nextPost;
    const myDescSplitted = blogPost?.description.split('<h2>');

    const loadMore = async () => {
        const newLimit = limit + BLOG_LIMIT_POSTS;

        try {
            const res = await getBlogPosts({ variables: { page: 1, limit: newLimit } });
            const newPosts = res.data.blogPosts;
            if (newPosts.length < newLimit) {
                setMaxPost(true);
            }
            setRecentPosts([...newPosts]);
            setLimit(newLimit);
        } catch (error) {
            console.warn(error);
        }
    };

    const handleGo = (e, slug) => {
        e.preventDefault();
        e.stopPropagation();
        window.location = slug;
    };

    useEffect(() => {
        if (data?.blogPost?.recentPosts) {
            setRecentPosts(data?.blogPost?.recentPosts);
            setMaxPost(data?.blogPost?.recentPosts?.length < limit);
        }
    }, [data?.blogPost]);

    if (!blogPost && !loadingPage) {
        return <NotFound />;
    } else if (!blogPost || loadingPage) {
        return <Preloader fullWindow />;
    }

    return (
        <div className={b()}>
            <BlogTopSticky {...{ useStateSSR }} />
            <BlogTopNav />
            <div className="d-none d-md-block b-bot mb-3" />
            <div className="desktop-container">
                <div className="mx-2 mx-md-0">
                    <SpotBreadcrumbs {...{ listBread, SSR: !!useStateSSR }} />
                </div>
            </div>
            <div className={b('content').mix('desktop-container')}>
                <div className="mx-2 mx-md-0">
                    <h1 className="snif-m1 snif-xl-pc snif-semibold snif-bold-pc mb-3 mb-md-4">{blogPost.title}</h1>
                    <BlogSubDetail {...{ isMobile, blogPost, updatedAt, useStateSSR }} />
                    {blogPost?.thumbnail && (
                        <PictureSet
                            className={b('thumb')}
                            imgs={thumbnail}
                            width="100%"
                            height={394}
                            useState={useStateSSR}
                            SSR={!!useStateSSR}
                            alt={blogPost.title + ' thumbnail'}
                            method="eager"
                        />
                    )}
                    <div className={b('desc')}>
                        {myDescSplitted?.map((d, i) => {
                            let text = i > 0 ? `<h2>${d}` : d;
                            const imgIdx = text.lastIndexOf('<figure');
                            const lastImg = text.lastIndexOf('</figure>') == text.length - 9;
                            const isShowBanner = i > 1 && i % 2 == 0;

                            if (imgIdx > 0 && lastImg) {
                                const t1 = text.slice(0, imgIdx);
                                const t2 = text.slice(imgIdx);
                                text = `${t1}${isShowBanner ? blogBanner(i) : ''}${t2}`;
                            } else {
                                text = `${text}${isShowBanner ? blogBanner(i) : ''}`;
                            }

                            return (
                                <React.Fragment key={i}>
                                    <div dangerouslySetInnerHTML={{ __html: text }} />
                                </React.Fragment>
                            );
                        })}
                    </div>
                    <BlogSubDetail {...{ isMobile, blogPost, updatedAt, useStateSSR }} noMargin />
                    <BlogAuthorDetails {...{ author: blogPost?.author, useStateSSR }} />
                </div>
                <BlogSideBar {...{ blogPost, useStateSSR }} />
            </div>
            <div className="desktop-container">
                <div className={b('prev-next-post')}>
                    {previousPost && (
                        <div className={b('prev-next-post_prev')} onClick={(e) => handleGo(e, previousPost.slug)}>
                            <SvgChevronLeft />
                            <div>
                                <p className="snif-xs snif-bold text-primary mb-1 pb-05 mb-md-2 pb-md-0">
                                    <a href={previousPost.slug}>PREVIOUS POST</a>
                                </p>
                                <BlogArticleSimple {...{ isMobile, data: previousPost, mobileSquare: true, useStateSSR }} />
                            </div>
                        </div>
                    )}
                    {nextPost && (
                        <div className={b('prev-next-post_next')} onClick={(e) => handleGo(e, nextPost.slug)}>
                            <div>
                                <p className="snif-xs snif-bold text-primary mb-1 pb-05 mb-md-2 pb-md-0">
                                    <a href={nextPost.slug}>NEXT POST</a>
                                </p>
                                <BlogArticleSimple {...{ isMobile, data: nextPost, mobileSquare: true, useStateSSR }} />
                            </div>
                            <SvgChevronRight />
                        </div>
                    )}
                </div>
                <div className="mx-2 mx-md-0">
                    <h2 className="snif-m2 snif-l2-pc snif-semibold snif-bold-pc mb-3 mb-md-5">Most recent articles</h2>
                    <div className={b('recent')}>
                        {recentPosts?.map((v) => (
                            <Fragment key={v.id}>
                                <BlogArticleSimple data={v} {...{ useStateSSR }} />
                                <BlogArticleDesktopLow data={v} {...{ useStateSSR }} />
                            </Fragment>
                        ))}
                    </div>
                    {!maxPost && (
                        <p className="text-center mb-5 pt-3 pb-2 pb-md-5">
                            <SniffButton size="lg" color="secondary" onClick={loadMore} disabled={loading}>
                                Load older articles
                            </SniffButton>
                        </p>
                    )}
                    <div className="d-md-none">
                        <h5 className="snif-m2 snif-semibold mb-3">Related articles</h5>
                        {blogPost?.relatedPosts.map((v) => (
                            <BlogArticleSimple key={v.id} data={v} {...{ useStateSSR }} />
                        ))}
                    </div>
                    <h2 className="snif-m2 snif-l2-pc snif-semibold snif-bold-pc mb-3 mb-md-5 pt-3 pt-md-0">Top dog guides per area</h2>
                    <BlogCategories categories={blogCategories} />
                </div>
            </div>
            <BlogFooter {...{ isMobile, useStateSSR }} />
            {!useStateSSR && <FooterNew isHome />}
        </div>
    );
};

export default BlogPost;
